import {RsnButton, RsnDialog, styles} from '@raison/ui-kit';
import React from 'react';
import {useNavigate} from 'react-router';
import {InfoPopup} from '~/entities/dialogs';
import {Blockchain} from '~/processes/crypto-sell-and-buy/model/Accounts';
import {FeeCalculationType} from '~/processes/crypto-send-and-receive/ui/SendTxIntro';

export function FeeHandlingDialog() {
    return (
        <RsnDialog.Window placement={'center'}>
            <RsnDialog.CenteredHeader>Fee handling</RsnDialog.CenteredHeader>
            <RsnDialog.Body align={'left'}>
                <div className={'flex flex-col gap-4'}>
                    <p className={'fv-text-l-regular text-center text-neutral-90'}>
                        How the transfer fee affects what you pay and what the recipient gets.
                    </p>
                    <div className={cls.box}>
                        <div>
                            <h3 className={cls.heading}>Included</h3>
                            <p className={cls.description}>
                                Fee is included in the amount you enter.{' '}
                                <span className={'whitespace-nowrap'}>The beneficiary</span> gets less.
                            </p>
                        </div>
                        <hr className={'border-b border-solid border-neutral-40'} />
                        <div>
                            <h3 className={cls.heading}>On top</h3>
                            <p className={cls.description}>
                                Fee is added on top of the amount you enter.{' '}
                                <span className={'whitespace-nowrap'}>The beneficiary</span> gets the full amount.
                            </p>
                        </div>
                    </div>
                </div>
            </RsnDialog.Body>
            <RsnDialog.Footer>
                <RsnButton.Alike intent='secondary' as={RsnDialog.CloseTrigger}>
                    I got it
                </RsnButton.Alike>
            </RsnDialog.Footer>
        </RsnDialog.Window>
    );
}

export type ConfirmDetailsDialogProps = {
    isOwnBlockchain: boolean;
    accountFrom: string;
    accountTo: string;
    feeCalculationType: FeeCalculationType;
    amountSold: string;
    amountSpend: string;
    fee: string;
    receive: string;
    onConfirm: () => void;
    blockchain: string;
};

export function ConfirmDetailsDialog({
    accountFrom,
    accountTo,
    feeCalculationType,
    amountSold,
    amountSpend,
    fee,
    receive,
    onConfirm,
    isOwnBlockchain,
    blockchain,
}: ConfirmDetailsDialogProps) {
    const feeHandling = (() => {
        switch (feeCalculationType) {
            case FeeCalculationType.Included:
                return 'Included';
            case FeeCalculationType.OnTop:
                return 'On top';
        }
    })();

    return (
        <RsnDialog.Window>
            <RsnDialog.CenteredHeader>Confirm details</RsnDialog.CenteredHeader>
            <RsnDialog.Body>
                <div className={cls.box}>
                    <div className={cls.detailsGroup}>
                        <KeyValueRow label={'From account'} value={accountFrom} />
                        <KeyValueRow label={'To account'} value={accountTo} />
                        {!isOwnBlockchain && <KeyValueRow label={'Pay fee from'} value={blockchain} />}
                    </div>
                    <div className={cls.detailsGroup}>
                        {isOwnBlockchain && <KeyValueRow label={'Fee handling'} value={feeHandling} />}
                        <KeyValueRow label={'Transaction fee'} value={fee} />
                        <KeyValueRow label={'Amount sold'} value={amountSold} />
                        {isOwnBlockchain && <KeyValueRow label={'You spend'} value={amountSpend} />}
                        <KeyValueRow label={'You receive'} value={receive} />
                    </div>
                </div>
            </RsnDialog.Body>
            <RsnDialog.Footer>
                <RsnButton.Primary onClick={onConfirm}>Confirm</RsnButton.Primary>
            </RsnDialog.Footer>
        </RsnDialog.Window>
    );
}

function KeyValueRow({label, value}: {label: string; value: string}) {
    return (
        <div className={'fv-text-m-regular flex justify-between'}>
            <div className={'text-neutral-60'}>{label}</div>
            <div className={'text-neutral-100'}>{value}</div>
        </div>
    );
}

export function NotEnoughFundsDialog({blockchain, pathToBuy}: {blockchain: Blockchain; pathToBuy: string | null}) {
    const {onClose} = RsnDialog.useControls();

    const navigate = useNavigate();

    const onBuy =
        pathToBuy !== null &&
        (() => {
            navigate(pathToBuy, {replace: true});
        });

    return (
        <InfoPopup
            type='warning'
            title={'Not enough funds'}
            details={'You don’t have enough ETH to cover the transaction fee.'}
            actions={{
                ...(onBuy && {primary: [`Buy ${blockchain.currencyCode}`, onBuy]}),
                secondary: ['I got it', onClose],
            }}
        />
    );
}

const cls = styles({
    heading: 'fv-text-l-bold text-[#0A0A0A]',
    description: 'fv-text-m-regular text-[#404040]',
    box: 'flex flex-col gap-4 rounded-[10px] bg-neutral-20 p-5',
    detailsGroup: 'flex flex-col gap-2',
});
